import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { noticesForRole, useNotify, type Notice, type NoticeKind } from "../lib/notify-store";
import { useRole } from "../lib/roles";
import { IconBell } from "./icons";

type Bucket = { id: string; title: string; items: Notice[] };

function kindLabel(kind: NoticeKind): string {
  if (kind === "editor_note" || kind === "changes") return "Needs attention";
  if (kind === "submitted") return "Review";
  if (kind === "approved") return "Approved";
  return "Campaign";
}

function buckets(notices: Notice[]): Bucket[] {
  const attention = notices.filter((n) => n.kind === "editor_note" || n.kind === "changes");
  const updates = notices.filter((n) => n.kind === "submitted" || n.kind === "approved");
  const campaigns = notices.filter((n) => n.kind === "campaign");
  return [
    { id: "attention", title: "Needs your attention", items: attention },
    { id: "updates", title: "Updates", items: updates },
    { id: "campaigns", title: "Campaigns", items: campaigns },
  ].filter((b) => b.items.length > 0);
}

function ago(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 0) return "";
  const m = Math.round(ms / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

export function NotificationBell() {
  const { role } = useRole();
  const { notices, markRead, markAllRead } = useNotify();
  const mine = noticesForRole(notices, role);
  const unread = mine.filter((n) => !n.read).length;
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const groups = buckets(mine);

  useEffect(() => { setOpen(false); }, [role]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="nl-bell" ref={wrapRef}>
      <button
        type="button"
        className="nl-icon-btn"
        aria-label={unread ? `${unread} unread notifications` : "Notifications"}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <IconBell />
        {unread > 0 && <span className="nl-bell-dot" aria-hidden>{unread > 9 ? "9+" : unread}</span>}
      </button>
      {open && (
        <div className="nl-notify-panel ui-enter" role="dialog" aria-label="Notifications">
          <div className="nl-notify-head">
            <div>
              <div className="nl-notify-title">Notifications</div>
              <div className="nl-notify-sub">{unread ? `${unread} unread` : "You're up to date"}</div>
            </div>
            <button
              type="button"
              className="nl-notify-mark"
              disabled={unread === 0}
              onClick={() => markAllRead(role)}
            >
              Mark all read
            </button>
          </div>
          <div className="nl-notify-list">
            {mine.length === 0 && (
              <div className="nl-notify-empty">No notes yet. Editor comments and review updates show up here.</div>
            )}
            {groups.map((group) => (
              <div key={group.id} className="nl-notify-group">
                <div className="nl-notify-group-title">{group.title}</div>
                {group.items.map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    className={`nl-notify-item${n.read ? "" : " is-unread"}`}
                    onClick={() => {
                      markRead(n.id);
                      setOpen(false);
                      if (n.href) navigate(n.href);
                    }}
                  >
                    <span className={`nl-notify-kind kind-${n.kind}`}>{kindLabel(n.kind)}</span>
                    <span className="nl-notify-item-title">{n.title}</span>
                    <span className="nl-notify-body">{n.body}</span>
                    <span className="nl-notify-time">{ago(n.at)}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
